import { useState } from 'react';
import PersonSearchModal from '../modals/PersonSearchModal';
import SelectTextField from '../../../molecules/SelectTextField';
import { Participant } from '../types/operations';

interface ParticipantsManagerProps {
    participants: Participant[];
    onAdd: (participant: Participant) => void;
    onRemove: (id: string) => void;
    participantTypes: Array<{ value: string; label: string }>;
    disabled?: boolean;
}

const ParticipantsManager: React.FC<ParticipantsManagerProps> = ({
    participants,
    onAdd,
    onRemove,
    participantTypes,
    disabled = false
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedParticipantType, setSelectedParticipantType] = useState('');

    const handleSelectPerson = (selected: any) => {
        if (!selectedParticipantType) {
            return;
        }

        const existingParticipant = participants.find(p => p.person_id === selected.value);
        if (existingParticipant) {
            return;
        }

        const [document, name] = selected.label.split(' - ');
        const newParticipant: Participant = {
            id: Date.now().toString(),
            person_id: selected.value,
            participant_type: selectedParticipantType as 'BORROWER' | 'GUARANTOR' | 'ASSIGNOR' | 'GUARANTOR-RETAIL' | 'RECIPIENT',
            name: name || '',
            document: document || ''
        };

        onAdd(newParticipant);
        setSelectedParticipantType('');
        setIsModalOpen(false);
    };

    return (
        <div>
            <h4 className="text-md font-semibold mb-4">Participantes</h4>

            <div className="grid grid-cols-10 gap-4 mb-4">
                <div className="col-span-9">
                    <SelectTextField
                        id="participant_type"
                        label="Tipo de Participante"
                        value={selectedParticipantType}
                        items={participantTypes}
                        onChange={(e) => setSelectedParticipantType(e.target.value)}
                        disabled={disabled}
                    />
                </div>
                <div className="col-span-1 flex items-end">
                    <button
                        type="button"
                        onClick={() => setIsModalOpen(true)}
                        disabled={!selectedParticipantType || disabled}
                        className="w-full btn-add"
                    >
                        Incluir
                    </button>
                </div>
            </div>

            <div className="mb-6">
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Documento</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Nome</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tipo</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {participants.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                        Nenhum participante adicionado
                                    </td>
                                </tr>
                            ) : (
                                participants.map((participant) => (
                                    <tr key={participant.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{participant.document}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{participant.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                            {participantTypes.find(t => t.value === participant.participant_type)?.label}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => onRemove(participant.id)}
                                                disabled={disabled}
                                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Remover
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <PersonSearchModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSelect={handleSelectPerson}
            />
        </div>
    );
};

export default ParticipantsManager;
